import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, QrCode, Clock, Edit, Lock, Trash2, Save, X, FileText, AlertCircle, CheckCircle2, Upload, Printer } from 'lucide-react';
import QRCode from 'qrcode.react';
import { supabase } from '../../lib/supabase';

const handlePrint = (maquinaria: any) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const qrValue = `${window.location.origin}/maquinarias/${maquinaria.id}/verify?qr=${maquinaria.clave_qr}`;

  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print QR Code</title>
        <meta charset="utf-8">
        <style>
          @page {
            size: 85mm 128mm;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            width: 85mm;
            height: 128mm;
            font-family: system-ui, -apple-system, sans-serif;
            background-color: #f5f5f5;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .container {
            width: 90%;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }
          .header {
            background: #004A98;
            color: white;
            padding: 12px 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 16px;
            font-weight: 500;
          }
          .content {
            padding: 24px;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .logo {
            width: 180px;
            margin-bottom: 24px;
          }
          .qr-code {
            margin: 12px 0;
          }
          .code-info {
            margin-top: 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }
          .code-wrapper {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;
            font-weight: 500;
          }
          .code-label {
            color: #6B7280;
            font-size: 12px;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div style="display: flex; align-items: center; gap: 8px;">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 3h18v18H3z"/>
                <path d="M7 7h10v10H7z"/>
              </svg>
              Código QR
            </div>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <div class="content">
            <img 
              src="https://emin.cl/wp-content/uploads/2025/Logo-Caemin-Grupo-Emin.png" 
              alt="CAEMIN Logo"
              class="logo"
            />
            <div class="qr-code" id="qr-container"></div>
            <div class="code-info">
              <div class="code-wrapper">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span>${maquinaria.clave_qr}</span>
              </div>
              <div class="code-label">Código de Acceso</div>
            </div>
          </div>
        </div>
        
        <script src="https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js"></script>
        <script>
          window.onload = function() {
            new QRCode(document.getElementById('qr-container'), {
              text: '${qrValue}',
              width: 180,
              height: 180,
              colorDark: '#000000',
              colorLight: '#ffffff',
              correctLevel: QRCode.CorrectLevel.H
            });
            
            setTimeout(() => {
              window.print();
              window.close();
            }, 1000);
          };
        </script>
      </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(printContent);
  printWindow.document.close();
};

interface Maquinaria {
  id: string;
  codigo: string;
  nombre: string;
  tipo: string;
  patente: string | null;
  marca: string | null;
  modelo: string | null;
  anio: number | null;
  numero_interno: string | null;
  clave_qr: string;
  created_at: string;
}

interface Horometro {
  id: string;
  lectura: number;
  fecha_lectura: string;
  alerta_mantenimiento: number;
}

interface Documento {
  id: string;
  tipo: string;
  storage_path: string | null;
  fecha_vencimiento: string | null;
}

const formatDate = (date: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getDocumentStatus = (doc: Documento) => {
  if (!doc.storage_path) {
    return { 
      status: 'missing', 
      color: 'red', 
      text: 'Sin archivo',
      icon: AlertCircle,
      bgColor: 'bg-red-100',
      textColor: 'text-red-800'
    };
  }

  if (!doc.fecha_vencimiento) {
    return { 
      status: 'valid', 
      color: 'green', 
      text: 'Vigente',
      icon: CheckCircle2,
      bgColor: 'bg-green-100',
      textColor: 'text-green-800'
    };
  }
  
  const vencimiento = new Date(doc.fecha_vencimiento);
  const today = new Date();
  const diffDays = Math.ceil((vencimiento.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    const diasVencido = Math.abs(diffDays);
    return { 
      status: 'expired', 
      color: 'red', 
      text: `Vencido hace ${diasVencido} día${diasVencido !== 1 ? 's' : ''}`,
      icon: AlertCircle,
      bgColor: 'bg-red-100',
      textColor: 'text-red-800'
    };
  }

  if (diffDays <= 30) {
    return { 
      status: 'warning', 
      color: 'yellow', 
      text: `Vence en ${diffDays} día${diffDays !== 1 ? 's' : ''}`,
      icon: Clock,
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800'
    };
  }

  return { 
    status: 'valid', 
    color: 'green', 
    text: 'Vigente',
    icon: CheckCircle2,
    bgColor: 'bg-green-100',
    textColor: 'text-green-800'
  };
};

const DocumentViewer = ({ documento }: { documento: Documento }) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  useEffect(() => {
    const getSignedUrl = async () => {
      if (!documento.storage_path) return;

      const { data: { publicUrl } } = supabase.storage
        .from('documentos')
        .getPublicUrl(documento.storage_path);

      if (publicUrl) {
        setSignedUrl(publicUrl);
      }
    };

    getSignedUrl();
  }, [documento]);

  if (!signedUrl) return null;

  return (
    <div className="w-full h-[600px] relative bg-gray-100 rounded-lg overflow-hidden">
      <iframe
        src={`${signedUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&download=0&view=FitH`}
        className="w-full h-full"
        title={documento.tipo}
        style={{
          border: 'none',
          backgroundColor: 'white'
        }}
      />
    </div>
  );
};

const DocumentUploadModal = ({ 
  isOpen, 
  onClose, 
  maquinariaId, 
  onUploadComplete 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  maquinariaId: string;
  onUploadComplete: () => void;
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [tipo, setTipo] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [existingDocument, setExistingDocument] = useState<any>(null);

  const checkExistingDocument = async (tipoDocumento: string) => {
    try {
      const { data, error } = await supabase
        .from('documentos')
        .select('*')
        .eq('maquinaria_id', maquinariaId)
        .eq('tipo', tipoDocumento)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (err) {
      console.error('Error checking existing document:', err);
      return null;
    }
  };

  const deleteOldDocument = async (documentId: string, storagePath: string | null) => {
    try {
      if (storagePath) {
        const { error: storageError } = await supabase.storage
          .from('documentos')
          .remove([storagePath]);

        if (storageError) {
          console.error('Error deleting old file from storage:', storageError);
        }
      }

      const { error: dbError } = await supabase
        .from('documentos')
        .delete()
        .eq('id', documentId);

      if (dbError) throw dbError;

    } catch (err) {
      console.error('Error deleting old document:', err);
      throw err;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file || !tipo) {
      setError('Por favor complete todos los campos requeridos');
      return;
    }

    if (file.type !== 'application/pdf') {
      setError('Solo se permiten archivos PDF');
      return;
    }

    const existing = await checkExistingDocument(tipo);
    
    if (existing && !showReplaceConfirm) {
      setExistingDocument(existing);
      setShowReplaceConfirm(true);
      return;
    }

    await uploadDocument();
  };

  const uploadDocument = async () => {
    setIsUploading(true);
    setError('');

    try {
      if (existingDocument) {
        await deleteOldDocument(existingDocument.id, existingDocument.storage_path);
      }

      const fileExt = file!.name.split('.').pop();
      const fileName = `${maquinariaId}/${Date.now()}_${tipo.replace(/\s+/g, '_')}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('documentos')
        .upload(fileName, file!, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data, error: dbError } = await supabase
        .from('documentos')
        .insert({
          maquinaria_id: maquinariaId,
          tipo,
          fecha_vencimiento: fechaVencimiento || null,
          storage_path: fileName
        })
        .select()
        .single();

      if (dbError) throw dbError;

      setFile(null);
      setTipo('');
      setFechaVencimiento('');
      setShowReplaceConfirm(false);
      setExistingDocument(null);
      
      onUploadComplete();
      onClose();
      
    } catch (err) {
      console.error('Error uploading document:', err);
      setError('Error al subir el documento');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setTipo('');
    setFechaVencimiento('');
    setError('');
    setShowReplaceConfirm(false);
    setExistingDocument(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {showReplaceConfirm ? 'Confirmar Reemplazo' : 'Subir Documento'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        {showReplaceConfirm ? (
          <div className="space-y-4">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">
                    Ya existe un documento de tipo "<strong>{tipo}</strong>" para esta maquinaria.
                  </p>
                  {existingDocument.fecha_vencimiento && (
                    <p className="text-sm text-yellow-700 mt-1">
                      Fecha de vencimiento actual: {new Date(existingDocument.fecha_vencimiento).toLocaleDateString('es-CL')}
                    </p>
                  )}
                  <p className="text-sm text-yellow-800 mt-2">
                    ¿Desea reemplazarlo con el nuevo documento?
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700">Nuevo documento:</p>
              <p className="text-sm text-gray-600">{file?.name}</p>
              {fechaVencimiento && (
                <p className="text-sm text-gray-600">
                  Nueva fecha de vencimiento: {new Date(fechaVencimiento).toLocaleDateString('es-CL')}
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowReplaceConfirm(false);
                  setExistingDocument(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Volver
              </button>
              <button
                onClick={uploadDocument}
                disabled={isUploading}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50"
              >
                {isUploading ? 'Reemplazando...' : 'Sí, Reemplazar'}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Tipo de Documento
              </label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                required
              >
                <option value="">Seleccionar tipo</option>
                <option value="Permiso de Circulación">Permiso de Circulación</option>
                <option value="Revisión Técnica">Revisión Técnica</option>
                <option value="Padrón">Padrón</option>
                <option value="Seguro">Seguro</option>
                <option value="Check List">Check List</option>
                <option value="Vial">Vial</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Fecha de Vencimiento
              </label>
              <input
                type="date"
                value={fechaVencimiento}
                onChange={(e) => setFechaVencimiento(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Opcional - Deje en blanco si el documento no tiene vencimiento
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Archivo PDF
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-medium
                  file:bg-[#004A98] file:text-white
                  hover:file:cursor-pointer hover:file:bg-[#003875]"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isUploading}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#004A98] hover:bg-[#003875] disabled:opacity-50"
              >
                {isUploading ? 'Subiendo...' : 'Subir'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export const MaquinariaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [maquinaria, setMaquinaria] = useState<Maquinaria | null>(null);
  const [horometro, setHorometro] = useState<Horometro | null>(null);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Documento | null>(null);
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Documento | null>(null);
  const [isEditingHorometro, setIsEditingHorometro] = useState(false);
  const [isSavingHorometro, setIsSavingHorometro] = useState(false);
  const [horometroError, setHorometroError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    codigo: '',
    nombre: '',
    tipo: '',
    patente: '',
    marca: '',
    modelo: '',
    anio: '',
    numero_interno: '',
    horometro: {
      lectura: 0,
      alerta_mantenimiento: 100
    }
  });

  const isQRView = searchParams.get('qr') !== null;

  const fetchDocumentos = async () => {
    if (!id) return;
    
    const { data: documentosData, error: documentosError } = await supabase
      .from('documentos')
      .select('*')
      .eq('maquinaria_id', id)
      .order('tipo');

    if (!documentosError && documentosData) {
      setDocumentos(documentosData);
    }
  };

  useEffect(() => {
    const fetchMaquinariaData = async () => {
      try {
        if (!id) return;

        const { data: maquinariaData, error: maquinariaError } = await supabase
          .from('maquinarias')
          .select('*')
          .eq('id', id)
          .single();

        if (maquinariaError) throw maquinariaError;
        
        setMaquinaria(maquinariaData);
        setEditForm({
          codigo: maquinariaData.codigo,
          nombre: maquinariaData.nombre,
          tipo: maquinariaData.tipo,
          patente: maquinariaData.patente || '',
          marca: maquinariaData.marca || '',
          modelo: maquinariaData.modelo || '',
          anio: maquinariaData.anio?.toString() || '',
          numero_interno: maquinariaData.numero_interno || '',
          horometro: {
            lectura: 0,
            alerta_mantenimiento: 100
          }
        });

        const { data: horometroData, error: horometroError } = await supabase
          .from('horometros')
          .select('*')
          .eq('maquinaria_id', id)
          .order('fecha_lectura', { ascending: false })
          .limit(1)
          .single();

        if (!horometroError && horometroData) {
          setHorometro(horometroData);
          setEditForm(prev => ({
            ...prev,
            horometro: {
              ...prev.horometro,
              lectura: horometroData.lectura,
              alerta_mantenimiento: horometroData.alerta_mantenimiento
            }
          }));
        }

        await fetchDocumentos();

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    fetchMaquinariaData();
  }, [id]);

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;

    setIsDeletingDoc(true);

    try {
      if (documentToDelete.storage_path) {
        const { error: storageError } = await supabase.storage
          .from('documentos')
          .remove([documentToDelete.storage_path]);

        if (storageError) {
          console.error('Error eliminando archivo del storage:', storageError);
        }
      }

      const { error: dbError } = await supabase
        .from('documentos')
        .delete()
        .eq('id', documentToDelete.id);

      if (dbError) throw dbError;

      await fetchDocumentos();
      setShowDeleteDocModal(false);
      setDocumentToDelete(null);
      
    } catch (err) {
      console.error('Error eliminando documento:', err);
      setError(err instanceof Error ? err.message : 'Error al eliminar el documento');
    } finally {
      setIsDeletingDoc(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      if (!id || !maquinaria) return;

      const { error: updateError } = await supabase
        .from('maquinarias')
        .update({
          codigo: editForm.codigo,
          nombre: editForm.nombre,
          tipo: editForm.tipo,
          patente: editForm.patente || null,
          marca: editForm.marca || null,
          modelo: editForm.modelo || null,
          anio: editForm.anio ? parseInt(editForm.anio) : null,
          numero_interno: editForm.numero_interno || null
        })
        .eq('id', id);

      if (updateError) throw updateError;

      if (horometro) {
        const { error: horometroError } = await supabase
          .from('horometros')
          .update({
            lectura: editForm.horometro.lectura,
            alerta_mantenimiento: editForm.horometro.alerta_mantenimiento
          })
          .eq('id', horometro.id);

        if (horometroError) throw horometroError;
      }
      
      setMaquinaria(prev => prev ? {
        ...prev,
        codigo: editForm.codigo,
        nombre: editForm.nombre,
        tipo: editForm.tipo,
        patente: editForm.patente || null,
        marca: editForm.marca || null,
        modelo: editForm.modelo || null,
        anio: editForm.anio ? parseInt(editForm.anio) : null,
        numero_interno: editForm.numero_interno || null
      } : null);
      
      if (horometro) {
        setHorometro(prev => prev ? {
          ...prev,
          lectura: editForm.horometro.lectura,
          alerta_mantenimiento: editForm.horometro.alerta_mantenimiento
        } : null);
      }
      
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating:', err);
      setError(err instanceof Error ? err.message : 'Error al actualizar la maquinaria');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      if (!id) return;

      const { error: deleteError } = await supabase
        .from('maquinarias')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      navigate('/maquinarias');
    } catch (err) {
      console.error('Error deleting:', err);
      setError(err instanceof Error ? err.message : 'Error al eliminar la maquinaria');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleHorometroSave = async (newReading: number) => {
    try {
      setIsSavingHorometro(true);
      setHorometroError(null);

      if (!id || !horometro) return;

      if (newReading < horometro.lectura) {
        setHorometroError('La nueva lectura no puede ser menor a la actual');
        return;
      }

      const { error: horometroError } = await supabase
        .from('horometros')
        .insert({
          maquinaria_id: id,
          lectura: newReading,
          alerta_mantenimiento: horometro.alerta_mantenimiento
        });

      if (horometroError) throw horometroError;

      setHorometro(prev => prev ? {
        ...prev,
        lectura: newReading
      } : null);

      setIsEditingHorometro(false);
    } catch (err) {
      console.error('Error updating horometer:', err);
      setHorometroError(err instanceof Error ? err.message : 'Error al actualizar el horómetro');
    } finally {
      setIsSavingHorometro(false);
    }
  };

  const renderHorometroSection = () => {
    if (!horometro) return null;

    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Estado del Horómetro</h3>
          {!isEditingHorometro && (
            <button
              onClick={() => setIsEditingHorometro(true)}
              className="flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <Edit className="h-4 w-4 mr-1" />
              Editar
            </button>
          )}
        </div>

        {isEditingHorometro ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nueva lectura (horas)
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  type="number"
                  min={horometro.lectura}
                  value={editForm.horometro.lectura}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    horometro: {
                      ...editForm.horometro,
                      lectura: parseInt(e.target.value) || 0
                    }
                  })}
                  className="flex-1 min-w-0 block w-full rounded-md border-gray-300 focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                />
              </div>
            </div>

            {horometroError && (
              <div className="text-sm text-red-600">
                {horometroError}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsEditingHorometro(false);
                  setHorometroError(null);
                  setEditForm(prev => ({
                    ...prev,
                    horometro: {
                      ...prev.horometro,
                      lectura: horometro.lectura
                    }
                  }));
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleHorometroSave(editForm.horometro.lectura)}
                disabled={isSavingHorometro}
                className="px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#004A98] hover:bg-[#003875] disabled:opacity-50"
              >
                {isSavingHorometro ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Lectura actual:</span>
              <span className="font-medium text-gray-900">{horometro.lectura} hrs</span>
            </div>
            <div className="mt-2">
              <div className="relative pt-1">
                <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                  <div
                    style={{
                      width: `${((horometro.lectura % horometro.alerta_mantenimiento) / horometro.alerta_mantenimiento) * 100}%`
                    }}
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[#004A98]"
                  ></div>
                </div>
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Próximo mantenimiento a las {horometro.lectura + (horometro.alerta_mantenimiento - (horometro.lectura % horometro.alerta_mantenimiento))} hrs
            </div>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004A98] mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando información...</p>
        </div>
      </div>
    );
  }

  if (error || !maquinaria) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error || 'Maquinaria no encontrada'}</p>
          {!isQRView && (
            <button
              onClick={() => navigate('/maquinarias')}
              className="mt-4 text-[#004A98] hover:underline"
            >
              Volver a la lista
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isQRView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="text-center mb-8">
            <img 
              src="https://emin.cl/wp-content/uploads/2025/Logo-Caemin-Grupo-Emin.png" 
              alt="CAEMIN Logo" 
              className="mx-auto h-16"
            />
          </div>

          <div className="bg-white shadow-sm rounded-lg p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">{maquinaria.nombre}</h1>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Información General</h3>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Código</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.codigo}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Tipo</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.tipo}</dd>
                  </div>
                </dl>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Información del Vehículo</h3>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Marca</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.marca || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Modelo</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.modelo || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Patente</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.patente || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Año</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.anio || '-'}</dd>
                  </div>
                </dl>
              </div>

              {horometro && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Estado del Horómetro</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Lectura actual:</span>
                      <span className="font-medium text-gray-900">{horometro.lectura} hrs</span>
                    </div>
                    <div className="mt-2">
                      <div className="relative pt-1">
                        <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                          <div
                            style={{
                              width: `${((horometro.lectura % horometro.alerta_mantenimiento) / horometro.alerta_mantenimiento) * 100}%`
                            }}
                            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[#004A98]"
                          ></div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      Próximo mantenimiento a las {horometro.lectura + (horometro.alerta_mantenimiento - (horometro.lectura % horometro.alerta_mantenimiento))} hrs
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow-sm rounded-lg p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/maquinarias')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Volver
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{maquinaria.nombre}</h1>
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#004A98] hover:bg-[#003875] disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Guardando...' : 'Guardar'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow-sm rounded-lg">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Información General</h3>
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Código</label>
                    <input
                      type="text"
                      value={editForm.codigo}
                      onChange={(e) => setEditForm({...editForm, codigo: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      value={editForm.nombre}
                      onChange={(e) => setEditForm({...editForm, nombre: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                    />
                  </div>
                </div>
              ) : (
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Código</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.codigo}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Nombre</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.nombre}</dd>
                  </div>
                </dl>
              )}
            </div>
          </div>

          <div className="bg-white shadow-sm rounded-lg">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Información del Vehículo</h3>
              {isEditing ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tipo</label>
                    <select
                      value={editForm.tipo}
                      onChange={(e) => setEditForm({...editForm, tipo: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                    >
                      <option value="Camión">Camión</option>
                      <option value="Excavadora">Excavadora</option>
                      <option value="Cargador Frontal">Cargador Frontal</option>
                      <option value="Retroexcavadora">Retroexcavadora</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Número Interno</label>
                    <input
                      type="text"
                      value={editForm.numero_interno}
                      onChange={(e) => setEditForm({...editForm, numero_interno: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Patente</label>
                    <input
                      type="text"
                      value={editForm.patente}
                      onChange={(e) => setEditForm({...editForm, patente: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Marca</label>
                    <input
                      type="text"
                      value={editForm.marca}
                      onChange={(e) => setEditForm({...editForm, marca: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Modelo</label>
                    <input
                      type="text"
                      value={editForm.modelo}
                      onChange={(e) => setEditForm({...editForm, modelo: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Año</label>
                    <select
                      value={editForm.anio}
                      onChange={(e) => setEditForm({...editForm, anio: e.target.value})}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                    >
                      <option value="">Seleccionar año</option>
                      {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Tipo</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.tipo}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Número Interno</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.numero_interno || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Patente</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.patente || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Marca</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.marca || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Modelo</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.modelo || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Año</dt>
                    <dd className="mt-1 text-sm text-gray-900">{maquinaria.anio || '-'}</dd>
                  </div>
                </dl>
              )}
            </div>
          </div>

          {/* Sección de Documentos */}
          <div className="bg-white shadow-sm rounded-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Documentos del Vehículo</h3>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#004A98]"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Subir documento
                </button>
              </div>

              {documentos.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No hay documentos</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Comience subiendo los documentos del vehículo.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#004A98] hover:bg-[#003875] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#004A98]"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Subir primer documento
                    </button>
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Documento</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Estado</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Vencimiento</th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">Acciones</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {documentos.map((documento) => {
                        const status = getDocumentStatus(documento);
                        const StatusIcon = status.icon;
                        return (
                          <tr key={documento.id} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                              <div className="flex items-center">
                                <FileText className="h-5 w-5 text-gray-400 mr-2" />
                                {documento.tipo}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.textColor}`}>
                                <StatusIcon className="h-4 w-4 mr-1" />
                                {status.text}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {formatDate(documento.fecha_vencimiento)}
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <div className="flex items-center justify-end space-x-2">
                                {documento.storage_path && (
                                  <button
                                    onClick={() => setSelectedDocument(documento)}
                                    className="text-[#004A98] hover:text-[#003875]"
                                  >
                                    Ver documento
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setDocumentToDelete(documento);
                                    setShowDeleteDocModal(true);
                                  }}
                                  className="text-red-600 hover:text-red-900 p-1"
                                  title="Eliminar documento"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="bg-[#004A98] px-4 py-3">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-white flex items-center">
                  <QrCode className="h-5 w-5 mr-2" />
                  Código QR
                </h3>
                <Lock className="h-5 w-5 text-white/80" title="Código de acceso requerido" />
              </div>
            </div>
            <div className="p-6">
              <div className="bg-white rounded-lg p-6">
                <div className="flex flex-col items-center">
                  <img 
                    src="https://emin.cl/wp-content/uploads/2025/Logo-Caemin-Grupo-Emin.png" 
                    alt="CAEMIN Logo" 
                    className="w-48 mb-8"
                  />
                  <QRCode 
                    value={`${window.location.origin}/maquinarias/${maquinaria.id}/verify?qr=${maquinaria.clave_qr}`} 
                    size={200} 
                    level="H"
                    bgColor="#ffffff" 
                    fgColor="#000000" 
                  />
                  <div className="mt-6">
                    <div className="flex items-center justify-center space-x-2">
                      <Lock className="h-4 w-4 text-gray-400" />
                      <p className="text-base font-medium text-gray-900">{maquinaria.clave_qr}</p>
                    </div>
                    <p className="text-sm text-gray-500 text-center mt-1">Código de Acceso</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handlePrint(maquinaria)}
                className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-[#004A98] text-white rounded-md hover:bg-[#003875] transition-colors duration-200"
              >
                <Printer className="h-4 w-4 mr-2" />
                Imprimir
              </button>
            </div>
          </div>

          {renderHorometroSection()}
        </div>
      </div>

      {/* Modal de confirmación para eliminar maquinaria */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Confirmar eliminación</h3>
            <p className="text-sm text-gray-500 mb-4">
              ¿Estás seguro de que deseas eliminar esta maquinaria? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para eliminar documento */}
      {showDeleteDocModal && documentToDelete && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Confirmar eliminación
              </h3>
              <button
                onClick={() => {
                  setShowDeleteDocModal(false);
                  setDocumentToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-red-50 rounded-lg p-4 mb-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      ¿Está seguro que desea eliminar este documento?
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>Esta acción eliminará permanentemente:</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Tipo de documento:</dt>
                    <dd className="text-sm text-gray-900 font-medium">{documentToDelete.tipo}</dd>
                  </div>
                  {documentToDelete.fecha_vencimiento && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Fecha de vencimiento:</dt>
                      <dd className="text-sm text-gray-900">
                        {formatDate(documentToDelete.fecha_vencimiento)}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Archivo:</dt>
                    <dd className="text-sm text-gray-900">
                      {documentToDelete.storage_path ? 'Archivo PDF adjunto' : 'Sin archivo'}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteDocModal(false);
                  setDocumentToDelete(null);
                }}
                disabled={isDeletingDoc}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDeleteDocument}
                disabled={isDeletingDoc}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isDeletingDoc ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Eliminando...
                  </span>
                ) : (
                  'Eliminar documento'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de subida de documentos */}
      {showUploadModal && (
        <DocumentUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          maquinariaId={id!}
          onUploadComplete={fetchDocumentos}
        />
      )}

      {/* Modal de visualización de documento */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{selectedDocument.tipo}</h3>
              <button
                onClick={() => setSelectedDocument(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <DocumentViewer documento={selectedDocument} />
          </div>
        </div>
      )}
    </div>
  );
};